import { z } from 'zod';
import { castFacingSchema, castIdSchema, castMoodSchema, castPoseSchema, castSlotSchema, castSoundSchema, propSlugSchema, scenePropSchema, } from './cast.js';
import { idSchema, paginated, paginationQuerySchema, slugSchema, } from './common.js';
import { lessonTermSchema } from './terms.js';
/**
 * The curriculum contract — tables in docs/specs/012-data-model.md, serving rules in
 * docs/decisions/api-conventions.md. Every field carries a `.describe()` because the
 * first consumer is a generator reading `openapi.json` (§9).
 *
 * This is the **served** shape; authoring inputs carry the keys and extend `sceneArms`
 * in CURR-08 rather than restating its bounds.
 *
 * Deliberately not in the package barrel — import `@platform/schema/content`. The
 * barrel is on the mobile boot path and Metro does not tree-shake.
 */
export const collectionIdSchema = idSchema
    .describe('A course, module or path.')
    .brand();
export const lessonIdSchema = idSchema
    .describe('A lesson.')
    .brand();
export const sceneIdSchema = idSchema
    .describe('One step in a lesson.')
    .brand();
export const sceneItemIdSchema = idSchema
    .describe('One option, rank item, bucket item or allocation category.')
    .brand();
export const sceneBucketIdSchema = idSchema
    .describe('A drop target on a BUCKET scene.')
    .brand();
export const questionIdSchema = idSchema
    .describe('A question in the bank, owned by no lesson.')
    .brand();
export const sourceIdSchema = idSchema
    .describe('An article, paper or link a lesson was generated from.')
    .brand();
export const subjectSchema = z
    .enum([
    'MARKETING',
    'FINANCE',
    'STRATEGY',
    'LEADERSHIP',
    'SALES',
    'NEGOTIATION',
    'ENTREPRENEURSHIP',
    'PUBLIC_SPEAKING',
    'COMMUNICATION',
    'OPERATIONS',
    'DECISIONS',
    'BUSINESS_FUNDAMENTALS',
    'TEAM_MANAGEMENT',
    'HIRING',
    'ORGANIZATIONAL_BEHAVIOR',
    'PRODUCT_MANAGEMENT',
    'AI',
    'INNOVATION',
    'PROJECT_MANAGEMENT',
    'BUSINESS_ANALYTICS',
    'CAREER',
    'PERSONAL_FINANCE',
    'ECONOMICS',
])
    .describe('The business subject a lesson or question teaches (005 §1), and the topic a learner picks in onboarding.');
/**
 * What each subject is called on screen — EXPL-04.
 *
 * Beside the enum for the reason `COLLECTION_NOUN` is: Explore prints it above a
 * course title so the subject is recognisable, and it is the one spelling every
 * other surface should reach for rather than respell. `PUBLIC_SPEAKING` is why —
 * a naive title-case gets it wrong. The console still prints the raw enum in its
 * filters and table; EXPL-05 routes it through here.
 */
export const SUBJECT_LABEL = {
    MARKETING: 'Marketing & Growth',
    FINANCE: 'Finance',
    STRATEGY: 'Strategy',
    LEADERSHIP: 'Leadership',
    SALES: 'Sales',
    NEGOTIATION: 'Negotiation',
    ENTREPRENEURSHIP: 'Entrepreneurship',
    PUBLIC_SPEAKING: 'Public Speaking & Presentations',
    COMMUNICATION: 'Business Communication',
    OPERATIONS: 'Operations',
    DECISIONS: 'Decision Making',
    BUSINESS_FUNDAMENTALS: 'Business Fundamentals',
    TEAM_MANAGEMENT: 'Team Management',
    HIRING: 'Hiring & Talent',
    ORGANIZATIONAL_BEHAVIOR: 'Organizational Behavior',
    PRODUCT_MANAGEMENT: 'Product Management',
    AI: 'AI',
    INNOVATION: 'Innovation',
    PROJECT_MANAGEMENT: 'Project Management',
    BUSINESS_ANALYTICS: 'Business Analytics',
    CAREER: 'Career & Personal Brand',
    PERSONAL_FINANCE: 'Personal Finance',
    ECONOMICS: 'Economics',
};
export const topicGroupSchema = z
    .enum(['BUSINESS', 'PEOPLE', 'PRODUCT', 'PLANNING', 'CAREER', 'FINANCE'])
    .describe('A heading the onboarding interests screen groups topics under.');
export const topicsResponseSchema = z
    .object({
    groups: z.array(z.object({
        id: topicGroupSchema,
        topics: z.array(subjectSchema).min(1),
    })),
})
    .describe('Every topic a learner can pick, grouped and ordered as onboarding shows them.');
/**
 * The onboarding spec's six groups, in its order, each topic in exactly one. An array
 * rather than a record because the order is what the screen shows.
 */
export const TOPIC_GROUPS = [
    {
        id: 'BUSINESS',
        topics: [
            'BUSINESS_FUNDAMENTALS',
            'ENTREPRENEURSHIP',
            'MARKETING',
            'SALES',
            'STRATEGY',
        ],
    },
    {
        id: 'PEOPLE',
        topics: [
            'LEADERSHIP',
            'TEAM_MANAGEMENT',
            'HIRING',
            'ORGANIZATIONAL_BEHAVIOR',
        ],
    },
    { id: 'PRODUCT', topics: ['PRODUCT_MANAGEMENT', 'AI', 'INNOVATION'] },
    {
        id: 'PLANNING',
        topics: [
            'PROJECT_MANAGEMENT',
            'OPERATIONS',
            'BUSINESS_ANALYTICS',
            'DECISIONS',
        ],
    },
    {
        id: 'CAREER',
        topics: ['CAREER', 'PUBLIC_SPEAKING', 'COMMUNICATION', 'NEGOTIATION'],
    },
    { id: 'FINANCE', topics: ['FINANCE', 'PERSONAL_FINANCE', 'ECONOMICS'] },
];
export const difficultySchema = z
    .enum(['BEGINNER', 'INTERMEDIATE', 'ADVANCED'])
    .describe('Who the content is for, authored rather than measured. Three values because neither a person nor a model applies a finer scale consistently.');
/**
 * A collection is as hard as its hardest lesson or module; undefined for none.
 *
 * Not an average: a module holding one ADVANCED lesson is not an INTERMEDIATE module to
 * the learner who reaches that lesson. Difficulty is a warning, and a warning rounds up.
 * The ordering is the schema's own, not a copy — GEN-34 renamed a second list and missed it.
 */
export const hardestOf = (levels) => levels.reduce((worst, level) => worst === undefined ||
    difficultySchema.options.indexOf(level) >
        difficultySchema.options.indexOf(worst)
    ? level
    : worst, undefined);
/**
 * Who content suits — spec 015, and 016 §2 for why the names are absent.
 *
 * 015 illustrates each archetype with a first name and an example job, and says in its
 * opening not to build for either. A `MAYA` member is how that gets built anyway.
 */
export const personaSchema = z
    .enum([
    'CAREER_STARTER',
    'RISING_MANAGER',
    'FUTURE_EXECUTIVE',
    'FOUNDER_OWNER',
    'EXPERIENCED_IC',
    'CAREER_SWITCHER',
])
    .describe('The learner archetype a lesson or collection suits (015). Broad archetypes, never a job title.');
export const personaFitSchema = z
    .enum(['IDEAL', 'SUITABLE', 'STRETCH', 'UNSUITABLE'])
    .describe('How well content suits a persona: written for it, usable by it, a reach for it, or wrong for it.');
/** One rule, so a slug cannot mean one thing here and another in `authoring.ts`. */
export const KEBAB_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * A persona this suits, optionally graded — 016 §5.
 *
 * Here rather than in `authoring.ts` because both directions need it: a write sets the
 * grade and the console has to read it back before it can edit it. `fit` is optional
 * rather than defaulted, so a row without one stays a plain label instead of showing a
 * reviewer a grade nobody chose.
 */
export const personaLabelSchema = z
    .strictObject({
    persona: personaSchema,
    fit: personaFitSchema.optional(),
})
    .describe('A persona this content suits.');
/**
 * The persona set a picker just produced, with any grade already set kept — 016 §5.
 *
 * Shared rather than console-local since GEN-70, which relabels from the lesson list. That
 * list carries the personas without their grades — the question a list answers is *who*,
 * not *how well* — so the merge has to happen where the grades are readable, which is the
 * API. Two spellings of it is how adding a persona comes to mean "clear every grade" on
 * one screen and not the other.
 */
export function mergedPersonas(current, next) {
    const byPersona = new Map(current.map((label) => [label.persona, label]));
    return next.map((persona) => byPersona.get(persona) ?? { persona });
}
/**
 * What identifies a tag — 016 §3.
 *
 * Two spellings of one idea is the drift `012` §19.6 refused tags over, so the slug is
 * the identity and the label is only what a human reads.
 */
export const tagSlugSchema = z
    .string()
    .min(2)
    .max(60)
    .regex(KEBAB_SLUG, 'lowercase words joined by single hyphens')
    .describe('Identifies a tag.');
export const collectionKindSchema = z
    .enum(['COURSE', 'MODULE', 'PATH'])
    .describe('What an ordered bag of lessons is for. A MODULE has a COURSE parent; a COURSE and a PATH have none.');
/**
 * What each kind is called in a sentence — PATH-01.
 *
 * Beside the enum because both sides of the wire say it: the console labels a settings
 * form and a checkbox with it, and the API puts it in front of the model ("looking at
 * this path"). Spelled separately they drift, and the drift is invisible — the operator
 * reads one word and the planner is told another.
 */
export const COLLECTION_NOUN = {
    COURSE: 'course',
    MODULE: 'module',
    PATH: 'path',
};
export const lessonFormatSchema = z
    .enum(['STANDARD', 'FLASHCARDS', 'DRILL'])
    .describe('How the player renders the same scenes: play and grade, reveal the key, or time every placement.');
export const contentStatusSchema = z
    .enum(['DRAFT', 'PUBLISHED', 'ARCHIVED'])
    .describe('The draft gate. Generated content lands as DRAFT and only a human moves it to PUBLISHED.');
export const reviewStatusSchema = z
    .enum(['NEEDS_REVIEW', 'APPROVED', 'NEEDS_CHANGES'])
    .describe('Whether an admin has checked a lesson against its sources.');
export const publishEventKindSchema = z
    .enum(['PUBLISHED', 'UNPUBLISHED'])
    .describe('A lesson going live, or coming down — CURR-20.');
export const lessonVersionReasonSchema = z
    .enum(['REBUILD', 'RESTORE', 'FIX', 'PUBLISH', 'REVIEW'])
    .describe("What replaced the content a lesson version holds — CURR-34 — or PUBLISH: what learners were served from that publish on, CURR-46, or REVIEW: what a build's review read, GEN-155.");
export const placementSyncSchema = z
    .enum(['UPDATE_PLAN', 'MOVE_APP_LESSON'])
    .describe('Which side a placement sync changes: the plan to match the app, or the app lesson to match the plan.');
export const sourceTypeSchema = z
    .enum(['ARTICLE', 'RESEARCH', 'LINK', 'TEXT'])
    .describe('What kind of outside material a lesson was generated from.');
export const rungSchema = z
    .enum([
    'RECOGNIZE',
    'DECIDE',
    'MANIPULATE',
    'CREATE',
    'SPEAK',
    'APPLY',
    'SIMULATE',
])
    .describe('The interaction ladder from 005 §3 — what the learner is practising, as opposed to what the finger does.');
export const sceneKindSchema = z
    .enum([
    'HOOK',
    'CONCEPT',
    'CASE',
    'ARTICLE',
    'RECAP',
    'REWARD',
    'CHOICE',
    'RANK',
    'BUCKET',
    'SLIDER',
    'ALLOCATE',
    'SPEAK',
])
    .describe('What a scene asks the finger to do. Narrative kinds, and the mechanics that grade.');
/**
 * What each scene kind is called where a person reads its name — GEN-75.
 *
 * Beside the enum for the reason `COLLECTION_NOUN` is: four surfaces name the same scene
 * and spelled separately they drift. Title Case in a chip, a dropdown and a sentence
 * alike, because the sentence is naming the type. Written out rather than derived from
 * the member so a twelfth kind can take a name its enum member does not spell.
 *
 * A learner never reads these — `docs/decisions/scene-vocabulary.md` draws that line.
 */
export const SCENE_KIND_NOUN = {
    HOOK: 'Hook',
    CONCEPT: 'Concept',
    CASE: 'Case',
    ARTICLE: 'Article',
    RECAP: 'Key Takeaway',
    REWARD: 'Reward',
    CHOICE: 'Choice',
    RANK: 'Rank',
    BUCKET: 'Bucket',
    SLIDER: 'Slider',
    ALLOCATE: 'Allocate',
    SPEAK: 'Speak',
};
/**
 * Which kind a `NarrativeScene` is, as that component names them.
 *
 * Mapped rather than lower-cased and cast, and here rather than in each client, because
 * the viewer mapped it and the player cast it and neither could see the other. One short
 * of the narrative kinds: REWARD is narrative and retired, so no client draws it as a
 * scene — `sceneAuthorable`.
 */
export const NARRATIVE_KIND = {
    HOOK: 'hook',
    CONCEPT: 'concept',
    CASE: 'case',
    ARTICLE: 'article',
    RECAP: 'recap',
};
/**
 * Lowercase, and a closed set, because both are what the component takes: `Verb` in
 * packages/ui is already a closed union and it is the prop type on `LessonCard` and
 * `SceneFrame`. Serving free text here would mean every screen case-folds and casts,
 * and an unrecognised verb would render as a blank tag — the exact failure VerbTag's
 * own docstring says the closed union exists to prevent.
 */
export const verbSchema = z
    .enum([
    'decide',
    'allocate',
    'rank',
    'match',
    'predict',
    'diagnose',
    'negotiate',
    'rephrase',
    'pitch',
    'speak',
    'simulate',
])
    .describe('The VerbTag word. Mirrors `Verb` in packages/ui.');
/**
 * Lowercase for the same reason as `verbSchema` — `ObjectName` in packages/ui.
 *
 * Listed in group order, and `ILLUSTRATION_GROUPS` in `illustration.ts` is the grouping
 * itself. The order is what lets a console dropdown print a heading whenever the group
 * changes instead of carrying a second copy of the table; `illustration.test.ts` asserts
 * the two agree, in both directions.
 */
export const illustrationObjectSchema = z
    .enum([
    // argument
    'balance',
    'funnel',
    'stack',
    'bridge',
    'steps',
    'podium',
    // measure
    'chart',
    'pie',
    'gauge',
    'magnifier',
    'beaker',
    'hourglass',
    'calendar',
    'clock',
    'stopwatch',
    // money
    'vault',
    'wallet',
    'receipt',
    'calculator',
    'ledger',
    // direction
    'compass',
    'telescope',
    'map',
    'flag',
    'chess',
    'target',
    // people
    'gavel',
    'puzzle',
    'team',
    'network',
    'roundtable',
    'mic',
    'iceberg',
    'chat',
    // operations
    'gears',
    'pipeline',
    'crate',
    'factory',
    'server',
    'spool',
    'clipboard',
    'tray',
    'basket',
    'folder',
    'bin',
    // momentum
    'lightbulb',
    'rocket',
    'key',
    'trophy',
    'briefcase',
])
    .describe('Which illustration object a card shows. A category signal, never an identity. Mirrors `ObjectName` in packages/ui, which draws every one of them.');
export const subjectFamilySchema = z
    .enum(['growth', 'finance', 'people'])
    .describe('The three colour families packages/tokens ships. Derived from subject, never stored. Mirrors `SubjectFamilyName` in packages/ui.');
/**
 * A course's two chosen colours — VIS-18. Lowercase so one colour has one spelling, and
 * stored as chosen: the interface derives readable shades from them, artwork keeps them.
 */
export const courseColourSchema = z.string().regex(/^#[0-9a-f]{6}$/);
export const coursePaletteSchema = z
    .object({
    primary: courseColourSchema.describe('The colour that leads, as lowercase six-digit hex.'),
    secondary: courseColourSchema.describe('Its supporting accent, as lowercase six-digit hex.'),
})
    .describe("A course's own pair. Absent where the course chose none, which wears its subject family.");
/**
 * What a piece of course-owned content is tinted with, served together wherever one is:
 * the family charts and fallbacks key on, and its course's pair where one was chosen.
 */
export const tintFields = {
    family: subjectFamilySchema,
    palette: coursePaletteSchema
        .optional()
        .describe("Its course's pair, which it wears in place of the family. Absent where none was chosen."),
};
/**
 * Twenty-three subjects, three colour families. 009 §4 gives this mapping in prose and
 * packages/tokens ships the three; storing the family alongside the subject would be
 * two sources of truth for one fact, so only the subject is stored.
 *
 * The `Record<Subject, …>` annotation is what makes a new subject a compile error
 * here rather than an undefined tint on a card.
 */
export const subjectFamily = {
    MARKETING: 'growth',
    STRATEGY: 'growth',
    ENTREPRENEURSHIP: 'growth',
    FINANCE: 'finance',
    OPERATIONS: 'finance',
    DECISIONS: 'finance',
    LEADERSHIP: 'people',
    SALES: 'people',
    NEGOTIATION: 'people',
    PUBLIC_SPEAKING: 'people',
    COMMUNICATION: 'people',
    BUSINESS_FUNDAMENTALS: 'growth',
    PRODUCT_MANAGEMENT: 'growth',
    AI: 'growth',
    INNOVATION: 'growth',
    PROJECT_MANAGEMENT: 'finance',
    BUSINESS_ANALYTICS: 'finance',
    PERSONAL_FINANCE: 'finance',
    ECONOMICS: 'finance',
    TEAM_MANAGEMENT: 'people',
    HIRING: 'people',
    ORGANIZATIONAL_BEHAVIOR: 'people',
    CAREER: 'people',
};
/**
 * Which key column each mechanic answers against — spec 012 §7's table, as data.
 *
 * `null` where there is no per-item key: a narrative scene has nothing to grade, and
 * SLIDER and SPEAK key off the scene rather than its items. Written once, because a
 * chain of `if`s over the same table lets a twelfth mechanic fall through as "keyed".
 */
export const sceneKeyColumn = {
    HOOK: null,
    CONCEPT: null,
    CASE: null,
    ARTICLE: null,
    RECAP: null,
    REWARD: null,
    CHOICE: 'isCorrect',
    RANK: 'correctPosition',
    BUCKET: 'correctBucketId',
    ALLOCATE: 'correctAmount',
    SLIDER: null,
    SPEAK: null,
};
/**
 * Which kinds claim something happened in the world.
 *
 * These two must name a `Source` before they publish. The rest can cite nothing —
 * demanding a citation everywhere teaches a generator to attach one to everything, and a
 * well-formed invention is the kind that survives review.
 *
 * A publish obligation, not a served field: `sceneArms.CASE` carries no source, so a
 * grounded CASE is what a reviewer checked and a learner never sees. ARTICLE serves its.
 *
 * A `Record` like `sceneRole`: a twelfth kind has to declare which it is.
 */
export const sceneAsserts = {
    HOOK: false,
    CONCEPT: false,
    CASE: true,
    ARTICLE: true,
    RECAP: false,
    REWARD: false,
    CHOICE: false,
    RANK: false,
    BUCKET: false,
    SLIDER: false,
    ALLOCATE: false,
    SPEAK: false,
};
/**
 * Which kinds count against the scene bounds (`SCENES_MIN`/`SCENES_MAX`) — CURR-12.
 *
 * `REWARD` reports what the attempt earned rather than teaching anything, so spending one
 * of the eight on it left no room for the `RECAP` that now closes the teaching. A `Record`
 * for the reason `sceneAsserts` is one: a thirteenth kind has to say which it is, where a
 * hand-kept `!== 'REWARD'` would quietly count it.
 */
export const sceneTeaches = {
    HOOK: true,
    CONCEPT: true,
    CASE: true,
    ARTICLE: true,
    RECAP: true,
    REWARD: false,
    CHOICE: true,
    RANK: true,
    BUCKET: true,
    SLIDER: true,
    ALLOCATE: true,
    SPEAK: true,
};
/** `sceneTeaches` as a filter, so what it keeps is typed as never a REWARD. */
export function isTeaching(scene) {
    return sceneTeaches[scene.kind];
}
/**
 * Which kinds an author may put in a lesson — GEN-91.
 *
 * `REWARD` is the one that may not: what a lesson earned is the learner's XP, streak and
 * badges, which `AttemptResult.reward` already carries, so a per-lesson scene saying
 * "Nice work" reported none of it and was written once per lesson. The closing teaching
 * scene is the `RECAP` — the Key Takeaway.
 *
 * Existing lessons keep their `REWARD` rows and the arm that parses them: this refuses
 * new ones rather than invalidating what is published. A `Record` for the reason
 * `sceneTeaches` is one — a thirteenth kind has to say whether it may be authored.
 */
export const sceneAuthorable = {
    HOOK: true,
    CONCEPT: true,
    CASE: true,
    ARTICLE: true,
    RECAP: true,
    REWARD: false,
    CHOICE: true,
    RANK: true,
    BUCKET: true,
    SLIDER: true,
    ALLOCATE: true,
    SPEAK: true,
};
/** The same set as values, in the enum's order — so a picker cannot drift from the type. */
export const authorableKinds = sceneKindSchema.options.filter((kind) => sceneAuthorable[kind]);
/** The narrowed enum, for a body or a control that may only carry an authorable kind. */
export const authorableKindSchema = sceneKindSchema
    .extract(authorableKinds)
    .describe('A scene kind an author may write. Every kind but the retired REWARD.');
/** How many of these scenes count against the scene bounds. */
export function teachingCount(scenes) {
    let count = 0;
    for (const scene of scenes)
        if (sceneTeaches[scene.kind])
            count += 1;
    return count;
}
/**
 * Which kinds grade and which only tell. A `Record` rather than a list of the six,
 * for the same reason as `subjectFamily`: a twelfth kind added to `sceneKindSchema`
 * fails to compile here, where a hand-kept subset would quietly answer "narrative"
 * and the scene would render and never grade.
 */
export const sceneRole = {
    HOOK: 'narrative',
    CONCEPT: 'narrative',
    CASE: 'narrative',
    ARTICLE: 'narrative',
    RECAP: 'narrative',
    REWARD: 'narrative',
    CHOICE: 'interactive',
    RANK: 'interactive',
    BUCKET: 'interactive',
    SLIDER: 'interactive',
    ALLOCATE: 'interactive',
    SPEAK: 'interactive',
};
/**
 * Which mechanics one gesture finishes, so the gesture may be the answer. CHOICE alone:
 * every other kind is several inputs, or a drag with no moment that reads as done.
 *
 * A `Record` for the reason `sceneRole` is one — a thirteenth kind has to declare which
 * it is. Read it through `commitsOnSelect`, never directly.
 * `docs/decisions/answering-on-selection.md`.
 */
export const sceneCommitsOnSelect = {
    HOOK: false,
    CONCEPT: false,
    CASE: false,
    ARTICLE: false,
    RECAP: false,
    REWARD: false,
    CHOICE: true,
    RANK: false,
    BUCKET: false,
    SLIDER: false,
    ALLOCATE: false,
    SPEAK: false,
};
/**
 * The verb a scene wears when it carries none.
 *
 * `verb` is optional and most content omits it, but a scene rendered in a frame needs
 * one — `009` calls the tag the structural device, and an untagged scene reads as a
 * different screen rather than as an untagged one. The mechanic is the honest answer:
 * a BUCKET scene is a match whatever it asks.
 *
 * Here rather than in the client that first wanted it, for the same reason as
 * `sceneRole`: two surfaces guessing separately is how the same scene ends up tagged
 * `predict` in a lesson and untagged in a short.
 */
export const fallbackVerb = {
    HOOK: 'predict',
    CONCEPT: 'diagnose',
    CASE: 'diagnose',
    ARTICLE: 'diagnose',
    RECAP: 'diagnose',
    REWARD: 'decide',
    CHOICE: 'decide',
    RANK: 'rank',
    BUCKET: 'match',
    SLIDER: 'predict',
    ALLOCATE: 'allocate',
    SPEAK: 'speak',
};
/** The kinds that grade, named — derived so a twelfth reaches every reader on its own. */
export const interactiveKinds = Object.entries(sceneRole)
    .filter(([, role]) => role === 'interactive')
    .map(([kind]) => kind);
export const sceneItemSchema = z
    .object({
    id: sceneItemIdSchema,
    label: z
        .string()
        .min(1)
        .max(200)
        .describe('What the learner reads on the item.'),
})
    .describe('One thing a learner manipulates. Identical across every mechanic — the key that grades it stays on the server.');
export const sceneBucketSchema = z
    .object({
    id: sceneBucketIdSchema,
    label: z
        .string()
        .min(1)
        .max(120)
        .describe('The bucket a learner drops items into.'),
})
    .describe('A drop target, for BUCKET scenes only.');
/**
 * A scene's visual, as the catalog can already draw it — LESSON-07, PROD-20.
 *
 * One arm per catalog component that can draw it, and no arm without one: the
 * ordering that stops a lesson carrying a shape no surface can draw
 * (`docs/decisions/scene-visuals.md`).
 *
 * The arms are jobs a reader has rather than chart types — no `data`, no `options`,
 * no `spec`. `caption` is required, and is both the line under the visual and its
 * accessible name: one nobody can state in a sentence is decoration.
 */
const visualCaption = z
    .string()
    .min(1)
    .max(120)
    .describe('What the visual says, in one line. Drawn, and read aloud.');
const visualLabel = z
    .string()
    .min(1)
    .max(60)
    .describe('What this part of the drawing is.');
const visualReadout = z
    .string()
    .min(1)
    .max(24)
    .describe('The readout. Authored, because the unit is content.');
const visualTotal = z.object({
    label: visualLabel,
    value: z.number(),
    display: visualReadout,
});
/** A name inside the drawing: a column, a quadrant, an axis end, a cell. */
const visualName = z.string().min(1).max(40);
const visualAxis = z.object({
    label: visualLabel,
    low: visualName,
    high: visualName,
});
/** A number beside its label, already formatted. Shared by `figures` and a situation's stakes. */
const visualFigureItem = z.object({
    label: z.string().min(1).max(60),
    value: z.string().min(1).max(24),
    // Mirrors `FigureTone` in packages/ui, whole, for the reason `verbSchema`
    // mirrors `Verb`: a partial copy drifts, and a tone with no colour is blank.
    tone: z
        .enum(['default', 'up', 'down', 'accent', 'streak'])
        .optional()
        .describe('How the number reads. Direction, never right and wrong.'),
});
/** Somebody in the situation. Faceless: the role is the name under the figure. */
const visualParty = z.object({
    name: visualLabel.describe('The role, not a person: Buyer, Your manager, You.'),
    you: z
        .boolean()
        .optional()
        .describe("The learner's own seat, drawn in the subject's colour."),
    character: castIdSchema
        .optional()
        .describe("Who plays the role, from the lesson's cast."),
    pose: castPoseSchema.optional(),
    facing: castFacingSchema.optional(),
    slot: castSlotSchema.optional(),
    reaction: castMoodSchema
        .optional()
        .describe('Played once after the learner answers, never before.'),
});
/** What sits beside the cast. A fourth crowds the figures out. */
const visualProps = z.array(propSlugSchema).min(1).max(3).optional();
/** Which of the two speaks, or is waited on. Sides rather than indices: an author reads them. */
const visualSide = z.enum(['left', 'right']);
/** One of the two options a versus weighs. Two or three words to a gain or a cost. */
const visualVersusOption = z.object({
    label: visualLabel,
    gains: z.array(visualName).min(1).max(3),
    costs: z.array(visualName).min(1).max(3),
});
/** The two visuals that seat characters, apart so the planner can be offered only these — CURR-49. */
const dialogueVisual = z.object({
    kind: z.literal('dialogue'),
    caption: visualCaption,
    left: visualParty,
    right: visualParty,
    lines: z
        .array(z
        .object({
        by: visualSide,
        text: z
            .string()
            .min(1)
            .max(80)
            .optional()
            .describe('A few words. A paragraph belongs in the prompt.'),
        sound: castSoundSchema
            .optional()
            .describe("Instead of text, on an animal's side."),
        mood: castMoodSchema.optional(),
        thought: z
            .boolean()
            .optional()
            .describe('Thought rather than said, drawn as a thought bubble.'),
    })
        // One field or the other rather than a union: the SDK flattens a union's
        // arms, and a line is still one thing a speaker says.
        .refine((line) => (line.text === undefined) !== (line.sound === undefined), 'A line is either text or a sound.')
        .refine((line) => line.thought !== true || line.text !== undefined, 'A thought is words, never a sound.'))
        .min(1)
        .max(4)
        .describe('In the order they are said.'),
    awaiting: visualSide
        .optional()
        .describe('Whose line is next, drawn as an empty bubble for the learner.'),
    props: visualProps,
});
const situationVisual = z.object({
    kind: z.literal('situation'),
    caption: visualCaption,
    cast: z
        .array(visualParty.extend({
        thought: z
            .string()
            .min(1)
            .max(80)
            .optional()
            .describe('One line this party thinks, in a thought bubble.'),
    }))
        .min(1)
        .max(3)
        .describe('One to three. More is a meeting, and a figure cannot seat it.'),
    stakes: z
        .array(visualFigureItem)
        .min(1)
        .max(4)
        .optional()
        .describe('What is at stake before the question: a deadline, an ask, a budget.'),
    props: visualProps,
});
export const figureVisualSchema = z
    .discriminatedUnion('kind', [dialogueVisual, situationVisual])
    .describe('Characters on screen: a dialogue between two, or a situation seating one to three.');
/** Two to four numbers as tiles, apart so the planner can be offered it beside the figures — GEN-199. */
export const figuresVisualSchema = z.object({
    kind: z.literal('figures'),
    caption: visualCaption,
    figures: z.array(visualFigureItem).min(2).max(4),
});
export const sceneVisualSchema = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('bars'),
        caption: visualCaption,
        max: z
            .number()
            .positive()
            .describe('The value a full track represents. Rows share one scale.'),
        rows: z
            .array(z.object({
            label: z.string().min(1).max(60),
            value: z.number(),
            previous: z
                .number()
                .optional()
                .describe('Where the value was before. Omit if nothing moved.'),
            display: z
                .string()
                .min(1)
                .max(24)
                .describe('The readout. Authored, because the unit is content.'),
        }))
            .min(1)
            .max(4),
    }),
    figuresVisualSchema,
    z.object({
        kind: z.literal('share'),
        caption: visualCaption,
        shares: z
            .array(z.object({
            label: visualLabel,
            value: z.number().min(0),
            display: visualReadout
                .optional()
                .describe('The readout. Omit and the part reads as its share.'),
        }))
            .min(2)
            .max(6)
            .describe('Six is the cap. A seventh class is a table, not a hue.'),
    }),
    z.object({
        kind: z.literal('funnel'),
        caption: visualCaption,
        stages: z
            .array(visualTotal.extend({ value: z.number().min(0) }))
            .min(2)
            .max(5)
            .describe('In the order they happen. The first stage is the whole.'),
    }),
    z.object({
        kind: z.literal('waterfall'),
        caption: visualCaption,
        start: visualTotal,
        steps: z
            .array(visualTotal.extend({
            value: z
                .number()
                .describe('Signed. What this step adds, or takes off.'),
        }))
            .min(1)
            .max(5),
        end: visualTotal,
    }),
    z.object({
        kind: z.literal('dumbbell'),
        caption: visualCaption,
        max: z.number(),
        min: z
            .number()
            .optional()
            .describe('Where the scale starts. Zero unless the band is narrow.'),
        rows: z
            .array(z.object({
            label: visualLabel,
            before: z.number(),
            after: z.number(),
            beforeDisplay: visualReadout,
            afterDisplay: visualReadout,
        }))
            .min(1)
            .max(4),
    }),
    z.object({
        kind: z.literal('matrix'),
        caption: visualCaption,
        x: visualAxis,
        y: visualAxis,
        quadrants: z
            .array(visualName)
            .length(4)
            .optional()
            .describe('Top-left, top-right, bottom-left, bottom-right.'),
        items: z
            .array(z.object({
            label: visualLabel,
            // Fractions rather than values: a 2x2 has no units on either axis,
            // and a version that took numbers is a scatter plot with them hidden.
            x: z.number().min(0).max(1),
            y: z.number().min(0).max(1),
        }))
            .min(1)
            .max(4),
    }),
    z.object({
        kind: z.literal('flow'),
        caption: visualCaption,
        steps: z
            .array(z.object({
            label: visualLabel,
            note: z
                .string()
                .min(1)
                .max(80)
                .optional()
                .describe('One line: what happens at this link, not why.'),
        }))
            .min(2)
            .max(5),
    }),
    z.object({
        kind: z.literal('trend'),
        caption: visualCaption,
        at: z
            .array(z.string().min(1).max(16))
            .min(2)
            .max(8)
            .describe('What the points are: quarters, weeks, rounds.'),
        min: z.number(),
        max: z.number(),
        series: z
            .array(z.object({
            label: visualLabel,
            values: z.array(z.number()).min(2).max(8),
            display: visualReadout.describe('The last value, formatted.'),
        }))
            .min(1)
            .max(6)
            .describe('One scale for all of them. There is no second axis.'),
    }),
    z.object({
        kind: z.literal('table'),
        caption: visualCaption,
        columns: z.array(visualName).min(2).max(3),
        rows: z
            .array(z.object({
            label: visualLabel,
            cells: z.array(visualName).max(3),
        }))
            .min(2)
            .max(6),
    }),
    dialogueVisual,
    situationVisual,
    z.object({
        kind: z.literal('versus'),
        caption: visualCaption,
        left: visualVersusOption,
        right: visualVersusOption,
    }),
    z.object({
        kind: z.literal('branch'),
        caption: visualCaption,
        fork: z
            .string()
            .min(1)
            .max(80)
            .describe('The decision, as a short question.'),
        // No `taken`: which way the learner went is their answer, handed to the
        // figure live by the run rather than authored onto the scene.
        branches: z
            .array(z.object({
            label: visualLabel,
            outcome: z
                .string()
                .min(1)
                .max(60)
                .describe('What happens next, in a few words.'),
            tone: z
                .enum(['up', 'down'])
                .optional()
                .describe('Where it leads. Direction, never right and wrong.'),
        }))
            .min(2)
            .max(3),
    }),
    z.object({
        kind: z.literal('timeline'),
        caption: visualCaption,
        moments: z
            .array(z.object({
            at: z.string().min(1).max(16).describe('When — Q1, Day 30, 2019.'),
            label: visualLabel,
        }))
            .min(2)
            .max(5)
            .describe('In order. A sixth leaves no width for its words.'),
        now: z
            .number()
            .int()
            .min(0)
            .optional()
            .describe('Which moment is the present. Absent, every moment is history.'),
    }),
    z.object({
        kind: z.literal('equation'),
        caption: visualCaption,
        // The numbers ride beside the displays, as the waterfall's do, so
        // `visualIssueParts` can refuse a sum that does not come out.
        start: visualTotal,
        steps: z
            .array(visualTotal.extend({
            op: z.enum(['plus', 'minus', 'times', 'divide']),
        }))
            .min(1)
            .max(2)
            .describe('One or two. A third leaves a term too narrow for its number.'),
        result: visualTotal,
    }),
    z.object({
        kind: z.literal('curve'),
        caption: visualCaption,
        // Its own range rather than the slider's: every other arm carries its own
        // scale, and a visual that read the scene's columns would only draw on one kind.
        price: z
            .object({ low: z.number(), high: z.number() })
            .describe('The range the curve is drawn across.'),
        demand: z
            .object({
            atLow: z.number().min(0),
            atHigh: z.number().min(0),
        })
            .describe('How many sell at each end. Straight between them.'),
        readoutLabel: visualLabel.describe('What the marker reads — Revenue.'),
        unit: z.string().max(8).describe('Written before the number — $ or £.'),
        from: visualName,
        to: visualName,
    }),
])
    .describe('A small visual above a scene. Takes the mascot’s place where a scene carries one.');
export const figureOf = (visual) => visual?.kind === 'dialogue' || visual?.kind === 'situation'
    ? visual
    : undefined;
/** Exactly as a picture's party names it, since the publish gate compares the two verbatim. */
export const castRoleSchema = visualLabel.describe('The role this lesson gives them, as a party names it: Buyer.');
/** A scene names a prop by slug; what the learner reads on its tag is the row's label. */
export const lessonPropsSchema = z
    .array(scenePropSchema)
    .optional()
    .describe('The props its scenes name. Absent when they name none.');
/** Who a lesson's story is about, and the role each plays in it — `docs/decisions/cast.md`. */
export const lessonCastSchema = z
    .array(z.strictObject({
    character: castIdSchema,
    role: castRoleSchema,
}))
    .max(3)
    .refine((cast) => new Set(cast.map((one) => one.character)).size === cast.length, 'A character plays one role in a lesson.')
    .describe('Up to three characters, each with the role they play.');
/** The column is Json, so the schema says what it holds; a row that no longer parses casts nobody. */
export const savedLessonCast = (json) => lessonCastSchema.safeParse(json).data ?? [];
/** Room for a hint of a few short paragraphs of `PARAGRAPH_WORDS_MAX` words. */
export const HINT_MAX = 600;
/**
 * The bounds a scene is both written and read against — GEN-153. A write cap looser than
 * its read cap publishes a lesson that 500s for every learner, so each lives here once.
 * The unit is 24 because generated lessons already hold units that long.
 */
export const SLIDER_UNIT_MAX = 24;
export const SCENE_BUCKETS_MAX = 4;
export const SCENE_ITEMS_MAX = {
    CHOICE: 8,
    RANK: 8,
    BUCKET: 12,
    ALLOCATE: 8,
};
const itemsMaxByKind = SCENE_ITEMS_MAX;
/** How many items a scene of this kind may hold, or `undefined` for a kind that shows none. */
export const sceneItemsMax = (kind) => itemsMaxByKind[kind];
/** The hint a learner may be shown: switched on and not blank. The API and the preview both ask. */
export function servedHint(scene) {
    return scene.hintEnabled && scene.hint?.trim() ? scene.hint : undefined;
}
/** The subheadline a learner may be shown: switched on and not blank. The API and the preview both ask. */
export function servedBody(scene) {
    return scene.bodyEnabled && scene.body?.trim() ? scene.body : undefined;
}
const sceneBase = {
    id: sceneIdSchema,
    position: z
        .number()
        .int()
        .min(1)
        .describe('Order within this lesson, from LessonScene.'),
    prompt: z
        .string()
        .min(1)
        .max(400)
        .describe('The words shown to the learner.'),
    verb: verbSchema.optional(),
    rung: rungSchema.optional(),
    timeLimitMs: z
        .number()
        .int()
        .min(1000)
        .optional()
        .describe('Set on the placement, not the scene: the same scene is untimed in its lesson and timed in a drill.'),
    visual: sceneVisualSchema
        .optional()
        .describe('On the base rather than the narrative arms: a mechanic scene is exactly where a trade-off wants drawing.'),
    hint: z
        .string()
        .min(1)
        .max(HINT_MAX)
        .optional()
        .describe('What the lightbulb beside the question opens: the question, clarified. Absent when the author left it off, and on a timed placement.'),
    body: z
        .string()
        .optional()
        .describe('The subheadline: short paragraphs under the headline, `**` for bold. Absent when the author switched it off or left it blank.'),
};
const narrativeBase = {
    ...sceneBase,
    imageUrl: z.url().optional().describe('Artwork for a narrative beat.'),
    objectName: illustrationObjectSchema
        .optional()
        .describe('The illustration this beat stands when it has no image of its own — GEN-120.'),
};
const narrative = (kind) => z.object({ ...narrativeBase, kind: z.literal(kind) });
/**
 * One arm per kind, named rather than inlined. `z.discriminatedUnion` needs an entry
 * per discriminator literal — a `.map()` collapses it to a union and breaks both
 * narrowing and `assertNever`, so the repetition is not removable.
 *
 * Exported because CURR-08's authoring shapes `.extend()` these. Restating them there
 * would put every bound in two places, and they would drift asymmetrically: raise one
 * on the write side alone and authors create lessons the read endpoint refuses.
 *
 * `satisfies Record<SceneKind, …>` makes a twelfth kind a compile error here.
 */
export const sceneArms = {
    HOOK: narrative('HOOK'),
    CONCEPT: narrative('CONCEPT'),
    CASE: narrative('CASE'),
    RECAP: narrative('RECAP'),
    REWARD: narrative('REWARD'),
    ARTICLE: z.object({
        ...narrativeBase,
        kind: z.literal('ARTICLE'),
        sourceId: sourceIdSchema,
        sourceTitle: z.string().min(1).describe('The work being cited.'),
        sourceUrl: z.url().optional().describe('Where to read it.'),
        citation: z.string().optional().describe('How to credit it.'),
    }),
    CHOICE: z.object({
        ...sceneBase,
        kind: z.literal('CHOICE'),
        options: z.array(sceneItemSchema).min(2).max(SCENE_ITEMS_MAX.CHOICE),
        maxSelections: z
            .number()
            .int()
            .min(1)
            .describe('1 for a single answer, more for multi-select.'),
        // On CHOICE alone, because CHOICE is the only kind `sceneCommitsOnSelect` marks:
        // everywhere else the control is already there and a flag asking for it says nothing.
        requireSubmit: z
            .boolean()
            .optional()
            .describe('Keep the submit control on a single-answer scene that would otherwise answer on selection.'),
    }),
    RANK: z.object({
        ...sceneBase,
        kind: z.literal('RANK'),
        items: z.array(sceneItemSchema).min(2).max(SCENE_ITEMS_MAX.RANK),
    }),
    BUCKET: z.object({
        ...sceneBase,
        kind: z.literal('BUCKET'),
        items: z.array(sceneItemSchema).min(2).max(SCENE_ITEMS_MAX.BUCKET),
        buckets: z.array(sceneBucketSchema).min(2).max(SCENE_BUCKETS_MAX),
    }),
    SLIDER: z.object({
        ...sceneBase,
        kind: z.literal('SLIDER'),
        min: z.number().describe('Left end of the track.'),
        max: z.number().describe('Right end of the track.'),
        step: z
            .number()
            .positive()
            .describe('Smallest movement the learner can make.'),
        unit: z
            .string()
            .max(SLIDER_UNIT_MAX)
            .describe('What the number is measured in — $, %, days.'),
    }),
    ALLOCATE: z.object({
        ...sceneBase,
        kind: z.literal('ALLOCATE'),
        categories: z.array(sceneItemSchema).min(2).max(SCENE_ITEMS_MAX.ALLOCATE),
        poolTotal: z
            .number()
            .positive()
            .describe('The budget the learner divides.'),
        unit: z
            .string()
            .max(SLIDER_UNIT_MAX)
            .optional()
            .describe('What the pool is counted in — $, hours. Absent for a pool of bare points.'),
    }),
    SPEAK: z.object({ ...sceneBase, kind: z.literal('SPEAK') }),
};
export const sceneSchema = z
    .discriminatedUnion('kind', [
    sceneArms.HOOK,
    sceneArms.CONCEPT,
    sceneArms.CASE,
    sceneArms.ARTICLE,
    sceneArms.RECAP,
    sceneArms.REWARD,
    sceneArms.CHOICE,
    sceneArms.RANK,
    sceneArms.BUCKET,
    sceneArms.SLIDER,
    sceneArms.ALLOCATE,
    sceneArms.SPEAK,
])
    .describe('One step in a lesson, as the client receives it — never with a key.');
/**
 * Whether this scene answers on selection, or waits for a control.
 *
 * The mechanic's default, narrowed by what the scene says: a multi-select is still being
 * built after the first tap, and `requireSubmit` buys the deliberate one back.
 *
 * Here rather than in the player, for the reason `sceneRole` is here — the lesson run and
 * Shorts both decide when to grade, and two surfaces guessing separately is how one scene
 * comes to submit on a tap in one and demand a button in the other.
 */
export function commitsOnSelect(scene) {
    if (!sceneCommitsOnSelect[scene.kind])
        return false;
    return (scene.kind !== 'CHOICE' ||
        (scene.maxSelections === 1 && scene.requireSubmit !== true));
}
export const questionSchema = z
    .object({
    id: questionIdSchema,
    subject: subjectSchema,
    difficulty: difficultySchema,
    stem: z
        .string()
        .min(1)
        .max(400)
        .describe('The knowledge being tested, stated once.'),
    explanation: z
        .string()
        .min(1)
        .describe('Why the answer is what it is. Shown after the learner acts, never before.'),
})
    .describe('A question, owned by nothing. The same one can be asked several ways.');
/** Shared by both card shapes so the four length bounds cannot drift apart. */
const cardBase = {
    slug: slugSchema,
    title: z.string().min(1).max(120).describe('What the card reads.'),
    summary: z
        .string()
        .min(1)
        .max(300)
        .describe('The one-line pitch under the title.'),
    difficulty: difficultySchema.optional(),
    objectName: illustrationObjectSchema.optional(),
};
export const lessonSummarySchema = z
    .object({
    ...cardBase,
    id: lessonIdSchema,
    format: lessonFormatSchema,
    verb: verbSchema.optional(),
    estimatedMinutes: z
        .number()
        .int()
        .min(1)
        .max(60)
        .describe('Roughly how long this takes.'),
})
    .describe('A lesson as it appears on a card — no scenes.');
export const lessonDetailSchema = lessonSummarySchema
    .extend({
    /**
     * The colour the player wears, derived from the lesson's collection the same
     * way Home and Shorts derive theirs. Served rather than left to the client:
     * `009` §4 puts subject colour on the surface, and the player is a surface.
     */
    ...tintFields,
    /**
     * The subject behind that colour, on the same grounds and from the same
     * relation. Many subjects map onto three families, so a client holding
     * `family` cannot recover it — and the player's narrative art keys on the
     * subject, not the tint.
     */
    subject: subjectSchema
        .optional()
        .describe('Absent when every collection owning the lesson is a path.'),
    scenes: z
        .array(sceneSchema)
        .describe('Every scene, in placement order. Never paginated — the player needs all of them.'),
    terms: z
        .array(lessonTermSchema)
        .describe('The terms this lesson uses, in order. Empty when it has none.'),
    cast: lessonCastSchema.optional().describe('Absent when it names nobody.'),
    props: lessonPropsSchema,
})
    .describe('The lesson player payload.');
export const collectionSummarySchema = z
    .object({
    ...cardBase,
    id: collectionIdSchema,
    kind: collectionKindSchema,
    subject: subjectSchema
        .optional()
        .describe('Absent on a PATH, which spans subjects and takes the brand colour.'),
    imageUrl: z
        .url()
        .optional()
        .describe('Bespoke art, where a course earns it.'),
    palette: coursePaletteSchema
        .optional()
        .describe("A course's own pair, or a module's course's. Absent on a path, whose lessons each wear their own."),
    lessonCount: z.number().int().min(0).describe('Derived, not stored.'),
})
    .describe('A course, module or path as it appears on a card.');
/**
 * A module, with what is in it.
 *
 * A course holds no lessons of its own — they hang off its modules — so a course
 * detail without this is a course with nothing in it, which is what the app showed.
 * A path's steps are modules too (PATH-05), so it is served the same way.
 * Carried in the parent's read rather than fetched per module: nesting is one level
 * deep by a CHECK in the migration, so this is bounded, and a screen that fetched a
 * request per module would draw a course in as many round trips as it has modules.
 */
const collectionModuleSchema = collectionSummarySchema
    .extend({
    lessons: z
        .array(lessonSummarySchema.extend(tintFields))
        .describe("In CollectionLesson order, each in its own course's tint — a path's modules come from several courses."),
})
    .describe('A module with its lessons.');
export const collectionDetailSchema = collectionSummarySchema
    .extend({
    children: z
        .array(collectionModuleSchema)
        .describe("Modules: a COURSE's own, or a PATH's in path order. Empty on a MODULE."),
    lessons: z
        .array(lessonSummarySchema.extend(tintFields))
        .describe("Placed directly on this collection — a MODULE's lessons — in CollectionLesson order, each in its own course's tint. A path's lessons are in its modules."),
})
    .describe('A collection with what it contains, one level deep.');
/**
 * Browse filters. Closed and strict, per docs/decisions/api-conventions.md §4: a
 * generic filter object would land in `openapi.json` as `filter: object`, which stops
 * `sdk:check` catching drift and types the generated SDK as `any`.
 *
 * `z.strictObject` rather than the default, so a field nobody declared is a 400 rather
 * than a filter that silently did nothing — the failure mode where a client believes
 * it narrowed a list and got everything.
 *
 * `status` is deliberately absent. It is not the caller's to choose: the read service
 * pins it to PUBLISHED and admin reads go through a different, role-gated service.
 */
export const collectionFilterSchema = z
    .strictObject({
    kind: collectionKindSchema.optional(),
    subject: subjectSchema.optional(),
    difficulty: difficultySchema.optional(),
    ...paginationQuerySchema.shape,
})
    .describe('Filters for GET /collections.');
/**
 * Filters for lessons, served from two transports and defined once.
 *
 * `GET /lessons` parses this from the query string and `POST /lessons/query` from the
 * body — api-conventions §4. It is one object rather than two that resemble each other,
 * so the drill builder and the browse screen cannot drift apart; the last three fields
 * reach through relations, which is what a `POST` body is for.
 */
export const lessonFilterSchema = z
    .strictObject({
    difficulty: difficultySchema.optional(),
    format: lessonFormatSchema.optional(),
    collectionSlug: z
        .string()
        .min(1)
        .max(80)
        .optional()
        .describe('Lessons placed in this course, module or path.'),
    subject: subjectSchema
        .optional()
        .describe('Lessons asking at least one question in this subject.'),
    sceneKind: sceneKindSchema
        .optional()
        .describe('Lessons containing at least one scene of this mechanic.'),
    ...paginationQuerySchema.shape,
})
    .describe('Filters for GET /lessons and POST /lessons/query.');
export const collectionPageSchema = paginated(collectionSummarySchema);
export const lessonPageSchema = paginated(lessonSummarySchema);
//# sourceMappingURL=content.js.map